import { useEffect, useState } from 'react';
import ValidatedInput, { ValidatedState } from '../custom-input/ValidatedInput';
import { Validators } from '../../types/enums/validators.enum';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../constants/common';
import './locationModal.scss';

export type SiteFormValues = {
  name: string;
  active: boolean;
};

type SiteModalProps = {
  mode: 'add' | 'edit';
  close: () => void;
  site?: SiteFormValues;
  code?: string;
  setSelectedId?: (id: number) => void;
  createSite?: (values: SiteFormValues) => Promise<number>;
  updateSite?: (values: SiteFormValues) => Promise<void>;
};

const SiteModal = ({ mode, close, site, code, setSelectedId, createSite, updateSite }: SiteModalProps): JSX.Element => {
  const [nameInput, setNameInput] = useState<ValidatedState>({ valid: mode === 'edit', value: site?.name ?? '' });
  const [activeInput, setActiveInput] = useState<boolean>(site?.active ?? true);
  const [validate, setValidate] = useState<boolean>(false);
  const [submit, setSubmit] = useState<boolean>(false);

  useEffect(() => {
    const action = async () => {
      const values: SiteFormValues = { name: nameInput.value.trim(), active: activeInput };
      try {
        if (mode === 'add' && createSite) {
          const id = await createSite(values);
          setSelectedId?.(id);
          showToast.success(toastMessages.SITE_CREATED_SUCCESS);
        } else if (mode === 'edit' && updateSite) {
          await updateSite(values);
          showToast.success(toastMessages.SITE_UPDATE_SUCCESS);
        }
      } catch {
        showToast.error(mode === 'add' ? toastMessages.SITE_CREATED_FAIL : toastMessages.SITE_UPDATE_FAIL);
      } finally {
        setSubmit(false);
        close();
      }
    };
    if (submit) {
      action();
    }
  }, [submit]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setValidate(true);
    if (nameInput.valid) {
      setSubmit(true);
    }
  };

  return (
    <div className="location-modal">
      <form onSubmit={handleSubmit}>
        <h2>{mode === 'add' ? LABEL_CONSTANTS.ADD_SITE : LABEL_CONSTANTS.EDIT_SITE}</h2>
        <hr />
        <p>{mode === 'add' ? LABEL_CONSTANTS.ADD_SITE_HEADER : LABEL_CONSTANTS.EDIT_SITE_HEADER}</p>
        {mode === 'edit' && code && (
          <div className="form-row">
            <label>{LABEL_CONSTANTS.SITE_CODE}:</label>
            <div className="input-container">
              <label className="location-code-value">{code}</label>
            </div>
          </div>
        )}
        <div className="validated-input-row">
          <ValidatedInput
            useId="site-name-input"
            type="text"
            label={LABEL_CONSTANTS.SITE_NAME}
            validate={validate}
            validatedStateForAutoFill={nameInput}
            setValidatedState={setNameInput}
            validators={[Validators.REQUIRED]}
          />
        </div>
        <div className="form-row">
          <label htmlFor="site-active-checkbox">{LABEL_CONSTANTS.ACTIVE}</label>
          <div className="input-container">
            <input
              id="site-active-checkbox"
              type="checkbox"
              defaultChecked={activeInput}
              onChange={e => setActiveInput(e.target.checked)}
            />
          </div>
        </div>
        <div className="flex-right submit-button location-modal-actions">
          <button type="button" className="small green inverted button" onClick={close}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          &emsp;
          <button type="submit" className={nameInput.valid ? 'small green button' : 'small green button disabled'}>
            {mode === 'add' ? BUTTON_CONSTANTS.SUBMIT : BUTTON_CONSTANTS.UPDATE}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SiteModal;
